import React, { useEffect, useState } from "react";
import { Edit, LogOut } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { completeRecruiterProfile, verifyRecruiterOTP } from "../../../redux/actions/halloffame";
import { toast } from "react-toastify";
import { Mixpanel } from '../../../config/Mixpanel';
import Constants from '../../../common/constants';
import { RESET_ALL_DATA, SET_RECRUITER_AUTH_DATA } from "../../../redux/actions/types";
import { useNavigate } from "react-router-dom";
import PhoneInput from "react-phone-number-input";
import { parsePhoneNumber } from "react-phone-number-input";
import { isValidPhoneNumber } from "libphonenumber-js/max";
import "react-phone-number-input/style.css";
import "./RecruiterDetails.css";

const RecruiterDetails = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const recruiterAccessToken = useSelector((state) => state.hallOfFame.recruiterAccessToken);
  const recruiterAuthData = useSelector((state) => state.hallOfFame.recruiterAuthData);

  const [phoneValue, setPhoneValue] = useState("");
  const [formData, setFormData] = useState({
    organisation: "",
    requirements: "",
  });
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [isVerified, setIsVerified] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setFormData((prev) => ({
        ...prev,
        organisation: prev.organisation,
        requirements: prev.requirements,
      }));
    }
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleChangeNumber = () => {
    setIsOtpSent(false);
    setOtp("");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const phone = typeof phoneValue === "string" ? phoneValue.trim() : "";
    if (!phone) {
      toast.error("WhatsApp phone number is required");
      return;
    }
    if (!isValidPhoneNumber(phone)) {
      toast.error("Please enter a valid phone number for the selected country");
      return;
    }
    if (!formData.organisation) {
      toast.error("Organisation is required");
      return;
    }
    let parsed;
    try {
      parsed = parsePhoneNumber(phone);
    } catch {
      toast.error("Please enter a valid phone number");
      return;
    }
    if (!parsed) {
      toast.error("Please enter a valid phone number");
      return;
    }
    const countryCode = `+${parsed.countryCallingCode}`;
    const nationalNumber = parsed.nationalNumber;
    dispatch(completeRecruiterProfile(
      recruiterAccessToken,
      countryCode,
      nationalNumber,
      formData.organisation,
      formData.requirements,
      (success) => {
        toast.success("OTP sent successfully");
        setIsOtpSent(true);
        console.log(success);
      },
      (error) => {
        console.log(error);
        toast.error(error?.response?.data?.detail);
      }
    ));
  };

  const handleOtpSubmit = (e) => {
    e.preventDefault();
    if (!otp) {
      toast.error("OTP is required");
      return;
    }
    const phoneForOtp = typeof phoneValue === "string" ? phoneValue.trim() : "";
    const parsedForOtp = phoneForOtp ? parsePhoneNumber(phoneForOtp) : null;
    const nationalNumberForOtp = parsedForOtp ? parsedForOtp.nationalNumber : phoneForOtp;
    dispatch(verifyRecruiterOTP(
      recruiterAccessToken,
      nationalNumberForOtp,
      otp,
      (success) => {
        console.log(success);
        toast.success("OTP verified successfully");
        setIsVerified(true);
        dispatch({ type: SET_RECRUITER_AUTH_DATA, payload: { ...recruiterAuthData, onboarding_required: false } });
        Mixpanel.track(Constants.Mixpanel.RECRUITER_COMPLETE_PROFILE_SUCCESS);
        onClose();
      },
      (error) => {
        console.log(error);
        setIsVerified(false);
      },
    ));
  };

  const handleSignOut = () => {
    if (recruiterAccessToken) {
      dispatch({ type: RESET_ALL_DATA });
      navigate('/recruiter');
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 z-[100] flex items-end justify-center transition-all duration-300 md:items-center ${isOpen ? "visible" : "invisible"
        }`}
    >
      {/* Backdrop */}
      <div
        className={`absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300 ${isOpen ? "opacity-100" : "opacity-0"
          }`}
      // onClick={onClose}
      />

      {/* Modal Container */}
      <div
        className={`relative w-full max-w-md overflow-hidden bg-[#0C4873] text-white shadow-2xl transition-all duration-300 md:rounded-2xl md:border md:border-slate-700 ${isOpen
          ? "translate-y-0 opacity-100 scale-100"
          : "translate-y-full opacity-0 md:translate-y-8 md:scale-95"
          } rounded-t-3xl md:translate-y-0`}
      >
        {/* Logout - top right */}
        <button
          type="button"
          onClick={handleSignOut}
          className="absolute right-4 top-4 rounded-full p-2 text-gray-400 hover:bg-white/10 hover:text-white transition-colors"
          aria-label="Sign out"
        >
          <LogOut size={20} />
        </button>

        <div className="p-6 md:p-8">
          {/* Header */}
          <h2 className="mb-6 text-xl font-bold leading-tight md:text-2xl text-left">
            You're Almost There!
          </h2>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <label className="font-inter text-sm font-medium text-gray-200" htmlFor="whatsapp">
                WhatsApp Phone No. <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2 items-center">
                <div className="recruiter-phone-input flex-1 min-w-0">
                  <PhoneInput
                    id="whatsapp"
                    international
                    defaultCountry="IN"
                    placeholder="Enter phone number"
                    value={phoneValue ?? ""}
                    onChange={(v) => setPhoneValue(v ?? "")}
                    disabled={isOtpSent && !isVerified}
                    numberInputProps={{
                      "aria-label": "WhatsApp phone number",
                      required: true,
                    }}
                    className="w-full"
                  />
                </div>
                {isOtpSent && !isVerified && (
                  <button
                    type="button"
                    onClick={handleChangeNumber}
                    className="shrink-0 p-2 rounded-lg text-gray-400 hover:bg-white/10 hover:text-white transition-colors"
                    aria-label="Change number"
                  >
                    <Edit size={20} />
                  </button>
                )}
              </div>
            </div>
            {isOtpSent && !isVerified && (
              <div className="space-y-1.5">
                <label className="font-inter text-sm font-medium text-gray-200" htmlFor="otp">
                  OTP
                  <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="otp"
                  name="otp"
                  placeholder="Enter OTP"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  className="w-full rounded-lg border border-[#2E4566] bg-[#132A4E] px-4 py-3 text-white placeholder-gray-500 focus:border-[#7fc241] focus:outline-none focus:ring-1 focus:ring-[#7fc241]"
                  required
                />
              </div>
            )}
            {isVerified && (
              <div className="space-y-1.5">
                <label className="font-inter text-sm font-medium text-gray-200" htmlFor="otp">
                  OTP
                </label>
                <p>OTP Verified</p>
              </div>
            )}

            <div className="space-y-1.5">
              <label className="font-inter text-sm font-medium text-gray-200" htmlFor="organisation">
                Organisation
                <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="organisation"
                name="organisation"
                placeholder="Enter Organisation Name"
                value={formData.organisation}
                onChange={handleChange}
                className="w-full rounded-lg border border-[#2E4566] bg-[#132A4E] px-4 py-3 text-white placeholder-gray-500 focus:border-[#7fc241] focus:outline-none focus:ring-1 focus:ring-[#7fc241]"
                required
              />
            </div>

            <div className="space-y-1.5">
              <label className="font-inter text-sm font-medium text-gray-200" htmlFor="requirements">
                Any Specific Requirements?
              </label>
              <textarea
                id="requirements"
                name="requirements"
                placeholder="Enter Any Recruiting Requirements here (optional)"
                value={formData.requirements}
                onChange={handleChange}
                rows={3}
                className="w-full rounded-lg border border-[#2E4566] bg-[#132A4E] px-4 py-3 text-white placeholder-gray-500 resize-none focus:border-[#7fc241] focus:outline-none focus:ring-1 focus:ring-[#7fc241]"
              />
            </div>

            <button
              type={isOtpSent && !isVerified ? "submit" : "button"}
              onClick={!isOtpSent && !isVerified ? handleSubmit : isOtpSent && !isVerified ? handleOtpSubmit : handleSubmit}
              className="mt-6 w-full rounded-lg bg-[#80C343] py-3.5 text-base font-bold text-black transition-transform hover:scale-[1.01] active:scale-[0.98] hover:bg-[#74b33a]"
            >
              {!isOtpSent && !isVerified ? "Send OTP" : isOtpSent && !isVerified ? "Verify OTP" : "Submit"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RecruiterDetails;
